/**
 * The building blocks every schema in this package is made of.
 *
 * Two directions, and they are not symmetric:
 *
 *   * **out** — parsing a response. A `Numeric(20, 4)` arrives as a JSON string (Pydantic
 *     serialises `Decimal` that way) and becomes a `Dec`. A `*_paise` column arrives as an
 *     integer and becomes a `Paise` bigint. Neither ever becomes a JS `number`.
 *   * **in** — building a request. A form gives us a string, a `Dec`, or occasionally a
 *     number; all three are normalised to the exact decimal string the server expects,
 *     after the same range checks Pydantic will apply. Failing here means the form shows
 *     the error without a round trip, which is the whole reason the schemas exist.
 */
import { z } from 'zod';
import { Dec } from '../numeric/decimal.js';
import { parsePaise } from '../numeric/money.js';
export const uuidSchema = z.string().uuid();
/** `YYYY-MM-DD`. The server's `date` columns, kept as strings — a JS `Date` is a moment
 *  in time with a timezone, and `movement_date` is a calendar day. Converting the two has
 *  put a receipt on the wrong day in more than one system. */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected a calendar date as YYYY-MM-DD');
/** An ISO-8601 instant, left as a string. Convert at the edge of a component if needed. */
export const isoDateTimeSchema = z.string().min(1);
/** Today, as the server's `date` shape, in the viewer's own timezone. */
export function today(now = new Date()) {
    const y = now.getFullYear().toString().padStart(4, '0');
    const m = (now.getMonth() + 1).toString().padStart(2, '0');
    const d = now.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
}
// ---------------------------------------------------------------------- outbound
const rawDecimal = z.union([z.string(), z.number()]);
/** A `Numeric` field on the way in from the server. Becomes an exact `Dec`. */
export const decimalOut = rawDecimal.transform((value, ctx) => {
    try {
        return Dec.fromJson(value);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `not a decimal: ${String(value)}` });
        return z.NEVER;
    }
});
export const nullableDecimalOut = z.union([decimalOut, z.null()]);
/** A `*_paise` column on the way in. Becomes a `Paise` bigint, never a `number`. */
export const paiseOut = z.union([z.string(), z.number()]).transform((value, ctx) => {
    try {
        return parsePaise(value);
    }
    catch {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `not an integer paise amount: ${String(value)}`,
        });
        return z.NEVER;
    }
});
/**
 * A decimal on the way OUT to the server, validated the way Pydantic will validate it and
 * emitted as an exact string.
 *
 * The string is what goes on the wire. Sending a JSON number here would hand the server a
 * float-derived value for a `Numeric(20, 4)` column — which is precisely the failure mode
 * invariant 5 exists to prevent, at the one place a UI is in a position to cause it.
 */
export function decimalIn(options = {}) {
    const { gt, gte, lte, maxPlaces = 4, label = 'value' } = options;
    return z
        .union([z.string().min(1, `${label} is required`), z.number(), z.custom((v) => v instanceof Dec, { message: 'expected a decimal' })])
        .transform((value, ctx) => {
        try {
            return value instanceof Dec ? value : Dec.fromJson(value);
        }
        catch {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${label} must be a number` });
            return z.NEVER;
        }
    })
        .superRefine((value, ctx) => {
        if (gt !== undefined && !value.gt(gt)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${label} must be greater than ${Dec.parse(gt).toString()}`,
            });
        }
        if (gte !== undefined && !value.gte(gte)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${label} must be at least ${Dec.parse(gte).toString()}`,
            });
        }
        if (lte !== undefined && !value.lte(lte)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${label} must be at most ${Dec.parse(lte).toString()}`,
            });
        }
        if (value.trim().scale > maxPlaces) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${label} may have at most ${maxPlaces} decimal places`,
            });
        }
    })
        .transform((value) => value.toString());
}
/** The shape every row read out of the store carries. */
export const readableSchema = z.object({
    id: uuidSchema,
    org_id: uuidSchema,
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
/** A bounded page size, matching the `Query(ge=..., le=...)` on the route. */
export function limitField(max, fallback) {
    return z.number().int().min(1).max(max).default(fallback);
}
export const offsetField = z.number().int().min(0).default(0);
//# sourceMappingURL=primitives.js.map